import { useEffect, useRef, useState } from "react";
import { normalizeHttpError } from "../../../lib/http/errors";
import { importBudgetConfirmation, previewBudgetConfirmationImport } from "../repo/budgetConfirmationImportRepo";
import type { BudgetImportPreview, BudgetImportRequest, BudgetImportResult } from "../repo/budgetConfirmationImportRepo";

export type BudgetConfirmationImportOptions = {
  budgetId: string; itineraryId: string; revision: string; blocked: boolean;
  onImported: (workspaceId: string) => void;
};

/** 管理预检、二次确认与导入会话。 */
export function useBudgetConfirmationImport(options: BudgetConfirmationImportOptions) {
  const [preview, setPreview] = useState<BudgetImportPreview | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<BudgetImportResult | null>(null);
  const active = useRef(options);
  active.current = options;
  const generation = useRef(0);
  const busy = useRef(false);
  const retry = useRef<BudgetImportRequest | null>(null);
  useEffect(() => {
    generation.current += 1;
    busy.current = false; retry.current = null;
    setPreview(null); setError(""); setResult(null); setPending(false);
    return () => { generation.current += 1; };
  }, [options.budgetId, options.itineraryId, options.revision]);
  const valid = (token: number) => generation.current === token && !active.current.blocked;
  const submit = async (body: BudgetImportRequest, token: number) => {
    if (!valid(token)) return;
    retry.current = body;
    const response = await importBudgetConfirmation(options.itineraryId, body);
    if (generation.current !== token) return;
    retry.current = null;
    setPreview(null); setResult(response);
    if (response.applied && response.workspace_id && valid(token)) active.current.onImported(response.workspace_id);
  };
  const failure = (cause: unknown, token: number) => {
    if (generation.current !== token) return;
    const normalized = normalizeHttpError(cause);
    if (!normalized.retriable) retry.current = null;
    setPreview(null);
    setError(normalized.message || "导入失败，请重试；原明细不会被部分替换");
  };
  const bodyFor = (value: BudgetImportPreview, overwrite: boolean): BudgetImportRequest => ({
    budget_order_id: options.budgetId, expected_source_revision: value.source_revision,
    expected_workspace_revision: value.workspace_revision, overwrite_confirmed: overwrite,
    request_id: globalThis.crypto?.randomUUID?.() ?? `import-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  });
  const start = async () => {
    if (active.current.blocked || busy.current) return;
    const token = generation.current;
    busy.current = true; setPending(true); setError(""); setResult(null);
    try {
      if (retry.current) { await submit(retry.current, token); return; }
      const value = await previewBudgetConfirmationImport(options.itineraryId, options.budgetId);
      if (!valid(token)) return;
      if (value.source_revision !== options.revision) throw new Error("核价数据已更新，请刷新当前版本后重新导入");
      if (value.existing_line_count > 0) setPreview(value);
      else await submit(bodyFor(value, false), token);
    } catch (cause) { failure(cause, token); }
    finally { if (generation.current === token) { busy.current = false; setPending(false); } }
  };
  const confirm = async () => {
    if (!preview || busy.current || active.current.blocked) return;
    const token = generation.current;
    busy.current = true; setPending(true); setError("");
    try { await submit(bodyFor(preview, true), token); }
    catch (cause) { failure(cause, token); }
    finally { if (generation.current === token) { busy.current = false; setPending(false); } }
  };
  return { preview, pending, error, result, start, confirm, cancel: () => { if (!busy.current) setPreview(null); } };
}
